import React from 'react';
import PropTypes from 'prop-types';
import _classNames from 'classnames';
import _isEmpty from 'lodash/isEmpty';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import ArrowRight from '@material-ui/icons/KeyboardArrowRight';
import { makeSelectClientDetails } from 'containers/Main/selectors';
import AppContainer from 'components/AppContainer';
import { getAccentColor } from 'containers/Main/utils';
import RichTextReactRenderer from 'components/RichTextReactRenderer';
import { getImageFile } from 'utils/stringUtils';
import useIntros from 'components/Hooks/useIntros';

const useStyles = makeStyles(theme => ({
  root: {
    borderRadius: 5,
    border: '1px solid #E9ECED',
    padding: '18px 16px !important',
    borderLeftWidth: 4,
    borderLeftColor: props => (props.accent ? `#${props.accent}` : '#E9ECED'),
  },
  reference: {
    marginTop: 16,
  },
  marginTop: {
    marginTop: 20,
  },
  marginBottom: {
    marginBottom: 20,
  },
  modal: {
    marginTop: 0,
    marginBottom: 12,
  },
  horizontalMargin: {
    marginLeft: 20,
    marginRight: 20,
  },
  image: {
    width: 64,
    height: 64,
    borderRadius: 12,
    marginRight: 18,
  },
  assessmentsColor: {
    borderColor: '#CDD0D1 !important',
  },
  link: {
    fontFamily: 'MadaBold',
  },
  caret: {
    height: 16,
    width: 16,
    marginBottom: 2,
  },
  textBackground: {
    padding: '16px !important',
    backgroundColor: '#F6F6F6',
  },
  plain: {
    marginTop: 20,
    marginBottom: 20,
  },
  referenceImageWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 128,
    height: 128,
  },
  referenceImage: {
    height: 'auto',
    maxHeight: '100%',
    maxWidth: '100%',
    objectFit: 'cover',
  },
  withImage: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2rem',
    alignItems: 'center',
    [theme.breakpoints.up('960')]: {
      flexDirection: 'row',
    },
  },
  withImageReversed: {
    flexDirection: 'column-reverse',
    [theme.breakpoints.up('960')]: {
      flexDirection: 'row-reverse',
    },
  },
}));

const PageIntroBlock = ({
  id,
  page,
  slug,
  type,
  modal,
  color,
  tags = [],
  isHomePage,
}) => {
  const clientDetails = useSelector(makeSelectClientDetails());
  const accentColor = getAccentColor(clientDetails, null);
  const classes = useStyles({ accent: accentColor });

  const { data: intros } = useIntros({
    type,
    page,
    tags,
    referenceId: id,
    referenceSlug: slug,
  });

  const [data] = intros || [];

  const renderContent = () => {
    if (_isEmpty(data)) return null;

    const { description, url, linkName, style } = data.fields;

    const textualLinkNode = url ? (
      <a
        href={url}
        target="_blank"
        aria-label={`Open ${linkName || 'Learn more'} in a new tab`}
        className={classes.link}
      >
        {linkName || 'Learn more'}
        <ArrowRight className={classes.caret} />
      </a>
    ) : null;

    if (style === 'card')
      return (
        <Grid item xs={12}>
          <div
            className={_classNames({
              [classes.root]: true,
              [classes.reference]: type === 'reference',
              [classes.marginTop]: [
                'sign-up',
                'sign-in',
                'assessment-save-intro',
                'user-resources',
              ].includes(page),
              [classes.marginBottom]: [
                'sign-up',
                'sign-in',
                'assessment-save-intro',
                'in-crisis',
                'assessment-results',
              ].includes(page),
              [classes.modal]: modal,
              [classes.horizontalMargin]: page === 'assessment-save-intro',
              [classes.assessmentsColor]:
                type === 'reference' && page === 'assessments',
            })}
          >
            {description ? (
              <RichTextReactRenderer
                color={color}
                data={description}
                paragraphVariant={
                  ['sign-up', 'sign-in', 'assessment-save-intro'].includes(page)
                    ? 'overline'
                    : 'body2'
                }
                textualLink={textualLinkNode}
                withHeaderStyles
                isSignUpPageIntro={page === 'sign-up'}
              />
            ) : (
              <>{textualLinkNode}</>
            )}
          </div>
        </Grid>
      );

    if (style === 'text')
      return (
        <Grid
          item
          xs={12}
          className={_classNames({
            [classes.textBackground]: true,
            [classes.reference]: type === 'reference',
            [classes.marginTop]: [
              'sign-up',
              'sign-in',
              'assessment-save-intro',
            ].includes(page),
            [classes.marginBottom]: [
              'sign-up',
              'sign-in',
              'in-crisis',
            ].includes(page),
            [classes.horizontalMargin]: page === 'assessment-save-intro',
          })}
        >
          <RichTextReactRenderer
            data={description}
            paragraphVariant="body2"
            textualLink={textualLinkNode}
            withHeaderStyles
            isSignUpPageIntro={page === 'sign-up'}
          />
        </Grid>
      );

    if (style === 'plain')
      return (
        <Grid item xs={12} className={classes.plain}>
          <RichTextReactRenderer
            color={color}
            data={description}
            paragraphVariant="body2"
            textualLink={textualLinkNode}
            withHeaderStyles
            isSignUpPageIntro={page === 'sign-up'}
          />
        </Grid>
      );

    if (style === 'image on left' || style === 'image on right') {
      const imageFile = getImageFile(data.fields);

      return (
        <Grid item xs={12} className={classes.plain}>
          <div
            className={_classNames({
              [classes.root]: true,
              [classes.reference]: type === 'reference',
              [classes.marginTop]: [
                'sign-up',
                'sign-in',
                'assessment-save-intro',
                'user-resources',
              ].includes(page),
              [classes.marginBottom]: [
                'sign-up',
                'sign-in',
                'assessment-save-intro',
                'in-crisis',
                'assessment-results',
              ].includes(page),
              [classes.modal]: modal,
              [classes.horizontalMargin]: page === 'assessment-save-intro',
              [classes.assessmentsColor]:
                type === 'reference' && page === 'assessments',
              [classes.withImage]: true,
              [classes.withImageReversed]: style === 'image on right',
            })}
          >
            <div className={classes.referenceImageWrapper}>
              <img src={imageFile} alt="" className={classes.referenceImage} />
            </div>

            <RichTextReactRenderer
              color={color}
              data={description}
              paragraphVariant="body2"
              textualLink={textualLinkNode}
              withHeaderStyles
              isSignUpPageIntro={page === 'sign-up'}
            />
          </div>
        </Grid>
      );
    }

    return null;
  };

  const content = renderContent();

  return isHomePage && content ? (
    <section id="page-intro-block">
      <AppContainer customPadding>{content}</AppContainer>
    </section>
  ) : (
    content
  );
};

PageIntroBlock.propTypes = {
  page: PropTypes.string,
  slug: PropTypes.string,
  id: PropTypes.string,
  type: PropTypes.string,
  modal: PropTypes.bool,
  color: PropTypes.string,
  tags: PropTypes.array,
  isHomePage: PropTypes.bool,
};

export default PageIntroBlock;
